/**
 * Assigning the alliance across a Bear Trap day's two traps.
 *
 * This replaces a picker that had no save button at all — a screen where an
 * operator ticked twenty names, generated a plan, and lost the roster on
 * reload. The assignment is now the saved artefact and the plan is derived from
 * it, which is the right way round: the alliance acts on who is in which trap.
 *
 * BT1 and BT2 are mutually exclusive by a game rule, not a preference. Taking
 * part in one trap starts a ~48h participation cooldown and both traps run the
 * same day (`strategy_rules BEAR_TRAP/PARTICIPATION_COOLDOWN`), so this is one
 * three-way choice per member rather than two independent checkboxes. Radios
 * make that visible in the control itself; two checkboxes would invite an
 * operator to tick both and be refused afterwards.
 *
 * Framed around who is coming, never around ranking members (spec section 50).
 */

import { useEffect, useMemo, useState } from 'react';
import type { BearDayAssignment, BearLeadPreference, PlayerSummary } from '@kso/schemas';
import {
  assignmentSignature,
  countAssignments,
  hasUnsavedChanges,
  splitEvenly as splitRows,
  toAssignmentPayload,
  toAssignmentState,
  type AssignmentRow,
  type AssignmentState,
  type TrapChoice,
  EMPTY_ROW,
} from './assignmentState';
import { Badge, Card, Notice, Value } from '../../components/ui/primitives';
import { ErrorState } from '../../components/ui/states';
import { formatCompact } from '../../lib/format';
import { useT } from '../../i18n';
import { useSetBearAssignments } from '../../api/queries';

const LEAD_PREFERENCES: readonly BearLeadPreference[] = ['AUTO', 'FORCE_LEAD', 'DO_NOT_LEAD'];

export function TrapAssignmentPicker({
  eventId,
  members,
  assignments,
}: {
  eventId: string;
  members: PlayerSummary[];
  assignments: readonly BearDayAssignment[];
}) {
  const t = useT();
  const save = useSetBearAssignments(eventId);

  const saved = useMemo(() => toAssignmentState(assignments), [assignments]);
  const [rows, setRows] = useState<AssignmentState>(saved);
  const [filter, setFilter] = useState('');

  /*
   * Re-seed from the server when what it holds actually CHANGES.
   *
   * Keyed on the content signature, not on the response's identity: a refetch
   * returning the same assignment produces a new array every time, and
   * re-seeding on that would erase a half-finished edit while the operator was
   * looking at it.
   *
   * When the content really has changed, the server's answer wins outright
   * rather than being merged. After a save that is simply the truth, and if
   * someone else edited the day, a merge would quietly resurrect local rows
   * nobody has reviewed against theirs.
   */
  const signature = useMemo(() => assignmentSignature(assignments), [assignments]);
  useEffect(() => {
    setRows(toAssignmentState(assignments));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the signature IS
    // the content of `assignments`; depending on the array would defeat this.
  }, [signature]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const matching = needle
      ? members.filter((member) => (member.name ?? member.id).toLowerCase().includes(needle))
      : members;
    return [...matching].sort((a, b) => (b.power ?? -1) - (a.power ?? -1));
  }, [members, filter]);

  const counts = useMemo(() => countAssignments(rows, members.length), [rows, members.length]);
  const dirty = hasUnsavedChanges(rows, saved);

  function setRow(playerId: string, patch: Partial<AssignmentRow>) {
    setRows((previous) => {
      const next = new Map(previous);
      next.set(playerId, { ...(next.get(playerId) ?? EMPTY_ROW), ...patch });
      return next;
    });
  }

  function submit() {
    save.mutate(toAssignmentPayload(rows));
  }

  return (
    <Card
      title={t.bearDay.assignmentTitle(counts.bt1, counts.bt2)}
      actions={
        <div className="row" style={{ gap: 4 }}>
          <input
            type="search"
            placeholder={t.planner.filterRoster}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            style={{ width: 160 }}
            aria-label={t.planner.filterRoster}
          />
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => setRows((previous) => splitRows(previous, visible.map((m) => m.id)))}
          >
            {t.bearDay.splitEvenly}
          </button>
          <button type="button" className="btn btn--sm" onClick={() => setRows(new Map())}>
            {t.common.clear}
          </button>
        </div>
      }
    >
      <p className="muted small" style={{ marginTop: 0 }}>
        {t.bearDay.assignmentBlurb}
      </p>

      <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table className="data">
          <thead>
            <tr>
              <th>{t.common.player}</th>
              <th className="num">{t.common.power}</th>
              <th style={{ width: 200 }}>{t.bearDay.trapColumn}</th>
              <th>{t.planner.leadDecision}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((member) => {
              const row = rows.get(member.id) ?? EMPTY_ROW;
              const name = member.name ?? member.id;
              return (
                <tr key={member.id}>
                  <td>
                    <Value absent={t.common.unnamedPlayer}>{member.name}</Value>
                  </td>
                  <td className="num">
                    {member.power === null ? (
                      <span className="absent">{t.common.unknown}</span>
                    ) : (
                      formatCompact(member.power)
                    )}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 10 }}>
                      {([null, 1, 2] as TrapChoice[]).map((choice) => (
                        <label key={String(choice)} className="row" style={{ gap: 4 }}>
                          <input
                            type="radio"
                            name={`trap-${member.id}`}
                            checked={row.trapNumber === choice}
                            onChange={() => setRow(member.id, { trapNumber: choice })}
                            aria-label={t.bearDay.trapChoiceAria(
                              name,
                              choice === null ? t.bearDay.neither : `BT${choice}`,
                            )}
                          />
                          <span className={choice === null ? 'muted small' : 'small'}>
                            {choice === null ? t.bearDay.neither : `BT${choice}`}
                          </span>
                        </label>
                      ))}
                    </div>
                  </td>
                  <td>
                    <select
                      value={row.leadPreference}
                      disabled={row.trapNumber === null}
                      onChange={(event) =>
                        setRow(member.id, {
                          leadPreference: event.target.value as BearLeadPreference,
                        })
                      }
                      aria-label={t.planner.leadDecisionAria(name)}
                    >
                      {LEAD_PREFERENCES.map((option) => (
                        <option key={option} value={option}>
                          {t.planner.leadPreference[option]}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {visible.length === 0 ? (
        <p className="absent small" style={{ marginBottom: 0 }}>
          {t.planner.noFilterMatch}
        </p>
      ) : null}

      <div className="row" style={{ marginTop: 14, gap: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn--primary"
          onClick={submit}
          disabled={save.isPending || !dirty}
        >
          {save.isPending ? t.bearDay.saving : t.bearDay.saveAssignment}
        </button>

        {dirty ? (
          <Badge tone="MEDIUM">{t.bearDay.unsavedChanges}</Badge>
        ) : save.isSuccess ? (
          <span className="muted small">{t.bearDay.savedAt(counts.bt1, counts.bt2)}</span>
        ) : null}

        <span className="muted small">{t.bearDay.unassignedCount(counts.unassigned)}</span>
      </div>

      {save.error ? (
        <div style={{ marginTop: 10 }}>
          <ErrorState error={save.error} />
        </div>
      ) : null}

      {save.data?.warnings.length ? (
        <div className="stack" style={{ marginTop: 10, gap: 6 }}>
          {save.data.warnings.map((warning) => (
            <Notice key={warning} severity="WARN">
              {warning}
            </Notice>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
